import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { Select } from "chakra-react-select";
import { default as React, useEffect, useState } from "react";
import { FaFileImage, FaTrash } from "react-icons/fa";
import { fetchTags } from "../../api/resources";
import { Context } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { ResourceTagManagment } from "./tagAdmin";

export const ResourcesItems = () => {
  const [loading, setLoading] = useState(true);
  const [resourceList, setResourceList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentResource, setCurrentResource] = useState({});
  const [toggleItems, setToggleItems] = useState(false);
  const [toggleTags, setToggleTags] = useState(false);

  const context = React.useContext(Context);

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    try {
      const compressedBlob = await imageCompression(file, options);

      const compressedFile = new File([compressedBlob], file.name, {
        type: compressedBlob.type,
      });

      return compressedFile;
    } catch (err) {
      console.error("Image compression failed", err);
      return file;
    }
  };

  const toggle = () => {
    setCurrentResource({});
    setIsModalOpen(!isModalOpen);
  };

  const startCreateResources = () => {
    setCurrentResource({});
    setIsModalOpen(true);
  };

  const startUpdateResources = (resourceData) => {
    setCurrentResource(resourceData);
    setIsModalOpen(true);
  };

  const toggleDelete = () => {
    setCurrentResource({});
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const startDeleteResources = (resourceData) => {
    setCurrentResource(resourceData);
    setIsDeleteModalOpen(true);
  };

  const toggleView = () => {
    setToggleItems(true);
  };

  const refreshResources = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v2/resources/", {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      setResourceList(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshResources();
  }, []);

  const handleSubmit = async (event, resource) => {
    event.preventDefault();
    setLoading(true);

    if (
      !resource.external_link &&
      !resource.resource_file &&
      !resource.is_workshop
    ) {
      context.fireToast("Error", "Resources must be set", "error");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("resource_title", resource.resource_title);
    formData.append("resource_description", resource.resource_description);
    formData.append("external_link", resource.external_link);
    formData.append("video_duration", resource.video_duration);
    formData.append("resource_type", resource.resource_type);
    formData.append("resource_link", resource.resource_link);
    formData.append("order", resource.order);
    formData.append("is_workshop", resource.is_workshop);
    resource.tags.forEach((tagId) => formData.append("tags", tagId));

    if (resource.card_image instanceof File)
      try {
        const compressedCardImage = await compressImage(resource.card_image);
        formData.append("card_image", compressedCardImage);
      } catch (error) {
        console.error("Card image compression failed:", error);
      }
    if (resource.resource_file instanceof File)
      formData.append("resource_file", resource.resource_file);

    const stepsPayload = resource.workshop_steps.map((step) => ({
      ...step,
      step_resources: step.step_resources.map((res) => {
        const copy = { ...res };

        if (!(res.card_image instanceof File)) {
          delete copy.card_image;
        }
        if (!(res.resource_file instanceof File)) {
          delete copy.resource_file;
        }

        return copy;
      }),
    }));

    formData.append("workshop_steps", JSON.stringify(stepsPayload));

    for (const [sIndex, step] of resource.workshop_steps.entries()) {
      for (const [rIndex, res] of step.step_resources.entries()) {
        if (res.card_image instanceof File) {
          try {
            const compressedCardImage = await compressImage(res.card_image);

            formData.append(
              `workshop_steps[${sIndex}][step_resources][${rIndex}][card_image]`,
              compressedCardImage
            );
          } catch (error) {
            console.error("Card image compression failed:", error);
          }
        }

        if (res.resource_file instanceof File) {
          formData.append(
            `workshop_steps[${sIndex}][step_resources][${rIndex}][resource_file]`,
            res.resource_file
          );
        }
      }
    }

    let url = `/api/v2/resources/`;
    try {
      let res;
      if (resource.id) {
        url += `${resource.id}/`;
        res = await axios.patch(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.status < 300) {
        context.fireToast(
          "Success",
          resource.id ? "Resource updated" : "Resource added",
          "success"
        );

        toggle();
        await refreshResources();
      } else {
        context.fireToast("Error", res.request.responseText, "error");
      }
    } catch (err) {
      console.error("Error submitting resource:", err);
      context.fireToast("Error", "Failed to save resource", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (resourceData) => {
    let url = `/api/v2/resources/`;

    url += `${resourceData.id}/`;

    axios
      .delete(url)
      .then((res) => {
        if (res.status < 300) {
          setResourceList(resourceList.filter((o) => o.id !== resourceData.id));
          context.fireToast("Success", "Resource has been deleted", "success");
        } else {
          context.fireToast("Error", res.request.responseText, "error");
        }
        setLoading(false);
        toggleDelete();
      })
      .catch((err) => console.log(err));
  };

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)">
      {!toggleItems && !toggleTags && (
        <>
          {loading && (
            <FontAwesomeIcon
              className="float-end fa-spin"
              icon={faSpinner}
              color="green"
            />
          )}
          <Stack direction="column" spacing={2} align="center">
            <CustomTableContainer width="100%">
              <ResourceModal
                isOpen={isModalOpen}
                toggle={toggle}
                title="Resource"
                resourceMainData={currentResource}
                loading={loading}
                handleSubmit={handleSubmit}
              />
              <ResourceDeleteModal
                colorScheme="red"
                isOpen={isDeleteModalOpen}
                toggle={toggleDelete}
                resourceMainData={currentResource}
                loading={loading}
                handleDelete={handleDelete}
              />
              <Flex pb="5">
                <Box>
                  <Heading as="h3" size="lg">
                    Resources
                  </Heading>
                </Box>
                <Spacer />
                <Box>
                  <HStack direction="row" spacing={6} align="center">
                    <Button onClick={() => setToggleTags(true)}>
                      Manage Resource Tags
                    </Button>
                    <Button
                      onClick={startCreateResources}
                      leftIcon={<AddIcon boxSize={3} />}
                    >
                      Create Resource
                    </Button>
                  </HStack>
                </Box>
              </Flex>
              <Box
                maxHeight="calc(100vh - 350px)"
                bg="white"
                overflowY={"auto"}
              >
                <Table variant="unstyled" overflowY={"auto"}>
                  <Thead position="sticky" top={-1} zIndex={1}>
                    <Tr>
                      <Th>Vault Title</Th>
                      <Th>Ordering</Th>
                      <Th>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {resourceList.map((resource) => (
                      <ResourceRow
                        key={resource.id}
                        resourceMainData={resource}
                        startUpdateResources={startUpdateResources}
                        toggle={toggleView}
                        startDeleteResources={startDeleteResources}
                      />
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CustomTableContainer>
          </Stack>
        </>
      )}{" "}
      {toggleTags && <ResourceTagManagment toggle={setToggleTags} />}
    </Container>
  );
};

const ResourceRow = ({
  resourceMainData,
  startUpdateResources,
  toggle,
  category,
  startDeleteResources,
}) => {
  return (
    <>
      <Tr key={resourceMainData.id}>
        <Td>{resourceMainData.resource_title}</Td>
        <Td>{resourceMainData.order}</Td>
        <Td>
          <HStack spacing={2}>
            <Button onClick={() => startUpdateResources(resourceMainData)}>
              Edit
            </Button>
            <Button
              colorScheme="red"
              onClick={() => startDeleteResources(resourceMainData)}
            >
              Delete
            </Button>
          </HStack>
        </Td>
      </Tr>
    </>
  );
};

const ResourceDeleteModal = ({
  isOpen,
  toggle,
  resourceMainData,
  loading,
  handleDelete,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={toggle}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Resource</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          Are you sure you want to delete this resource {resourceMainData.name}?
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="red"
            onClick={() => handleDelete(resourceMainData)}
            isDisabled={loading}
          >
            {loading ? "Processing..." : "Delete"}
          </Button>
          <Button onClick={toggle} ml={3}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ResourceModal = ({
  isOpen,
  toggle,
  title,
  resourceMainData,
  loading,
  handleSubmit,
}) => {
  const confirmText = !resourceMainData?.id ? "Create" : "Update";
  const context = React.useContext(Context);

  const [resourceData, setResourceData] = useState({
    resource_title: "",
    resource_description: "",
    external_link: "",
    video_duration: "",
    is_active: true,
    tags: [],
    card_image: "",
    resource_file: "",
    resource_link: "",
    resource_type: "internal",
    order: "",
    is_workshop: false,
    workshop_steps: [],
  });
  const [tagList, setTagList] = useState([]);
  const [preview, setPreview] = useState(null);
  const [fileAttached, setFileAttached] = useState(null);
  const [stepPreviews, setStepPreviews] = useState({});

  useEffect(() => {
    const fetchData = async () => setTagList(await fetchTags());
    fetchData();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setResourceData({
        resource_title: "",
        resource_description: "",
        external_link: "",
        video_duration: "",
        tags: [],
        card_image: "",
        resource_file: "",
        resource_link: "",
        resource_type: "internal",
        order: "",
        is_workshop: false,
        workshop_steps: [],
      });
      setPreview(null);
      setFileAttached(null);
      setStepPreviews({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (resourceMainData?.id) {
      const normalizedSteps = (resourceMainData.workshop_steps || []).map(
        (step, sIdx) => ({
          ...step,
          is_active: step.is_active !== false,
          label: step.label ?? `Step ${sIdx + 1}`,
          order: typeof step.order === "number" ? step.order : sIdx + 1,
          step_resources: (step.step_resources || []).map((res, rIdx) => ({
            ...res,
            is_active: res.is_active !== false,
            order: typeof res.order === "number" ? res.order : rIdx + 1,
          })),
        })
      );

      setResourceData({
        ...resourceMainData,
        workshop_steps: normalizedSteps,
      });
      setPreview(resourceMainData.card_image || null);
      setFileAttached(resourceMainData.resource_file || null);
    }
  }, [resourceMainData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setResourceData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMultiSelect = (selectedOptions) => {
    if (selectedOptions.length > 2) return;
    setResourceData((prev) => ({
      ...prev,
      tags: selectedOptions.map((opt) => opt.value),
    }));
  };

  const handleFileChange = (e, stepIndex = null, resIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      context.fireToast(
        "Error",
        "Only PDF, images (jpg/png), Excel (xls/xlsx) or CSV allowed",
        "error"
      );
      return;
    }

    if (stepIndex === null) {
      setResourceData((prev) => ({ ...prev, resource_file: file }));
      setFileAttached(file.name);
    } else {
      const steps = [...resourceData.workshop_steps];
      steps[stepIndex].step_resources[resIndex].resource_file = file;
      setResourceData((prev) => ({ ...prev, workshop_steps: steps }));
    }
  };

  const handleRemoveFile = (stepIndex = null, resIndex = null) => {
    if (stepIndex === null) {
      setResourceData((prev) => ({ ...prev, resource_file: "" }));
      setFileAttached(null);
    } else {
      const steps = [...resourceData.workshop_steps];
      steps[stepIndex].step_resources[resIndex].resource_file = null;
      setResourceData((prev) => ({ ...prev, workshop_steps: steps }));
    }
  };

  const handleImageChange = (e, stepIndex = null, resIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);

    if (stepIndex === null) {
      setPreview(previewUrl);
      setResourceData((prev) => ({ ...prev, card_image: file }));
    } else {
      const key = `${stepIndex}_${resIndex}`;
      setStepPreviews((prev) => ({ ...prev, [key]: previewUrl }));

      const steps = [...resourceData.workshop_steps];
      steps[stepIndex].step_resources[resIndex].card_image = file;
      setResourceData((prev) => ({ ...prev, workshop_steps: steps }));
    }
  };

  const removeImage = (stepIndex = null, resIndex = null) => {
    if (stepIndex === null) {
      setPreview(null);
      setResourceData((prev) => ({ ...prev, card_image: "" }));
    } else {
      const key = `${stepIndex}_${resIndex}`;
      setStepPreviews((prev) => ({ ...prev, [key]: null }));
      const steps = [...resourceData.workshop_steps];
      steps[stepIndex].step_resources[resIndex].card_image = "";
      setResourceData((prev) => ({ ...prev, workshop_steps: steps }));
    }
  };

  const addStep = () => {
    setResourceData((prev) => {
      const nextOrder = prev.workshop_steps.length + 1;
      return {
        ...prev,
        workshop_steps: [
          ...prev.workshop_steps,
          {
            label: `Step ${nextOrder}`,
            title: "",
            description: "",
            order: nextOrder,
            is_active: true,
            step_resources: [],
          },
        ],
      };
    });
  };

  const handleStepChange = (index, field, value) => {
    setResourceData((prev) => {
      const steps = prev.workshop_steps.map((step, sIdx) => {
        if (sIdx !== index) return step;
        return { ...step, [field]: field === "order" ? Number(value) : value };
      });
      return { ...prev, workshop_steps: steps };
    });
  };

  const handleStepResourceChange = (stepIndex, resIndex, field, value) => {
    setResourceData((prev) => {
      const steps = prev.workshop_steps.map((step, sIdx) => {
        if (sIdx !== stepIndex) return step;
        const step_resources = step.step_resources.map((res, rIdx) => {
          if (rIdx !== resIndex) return res;
          return {
            ...res,
            [field]: field === "order" ? Number(value || "0") : value,
          };
        });
        return { ...step, step_resources };
      });
      return { ...prev, workshop_steps: steps };
    });
  };

  const removeStep = (index) => {
    setResourceData((prev) => {
      const steps = prev.workshop_steps.map((step, sIdx) =>
        sIdx === index ? { ...step, is_active: false } : step
      );

      let order = 1;
      const renumberedSteps = steps.map((s) => {
        if (s.is_active !== false) return { ...s, order: order++ };
        return s;
      });

      return { ...prev, workshop_steps: renumberedSteps };
    });
  };

  const removeStepResource = (stepIndex, resIndex) => {
    setResourceData((prev) => {
      const steps = prev.workshop_steps.map((step, sIdx) => {
        if (sIdx !== stepIndex) return step;
        const step_resources = step.step_resources.map((res, rIdx) =>
          rIdx === resIndex ? { ...res, is_active: false } : res
        );
        return { ...step, step_resources };
      });
      return { ...prev, workshop_steps: steps };
    });
  };

  const addStepResource = (stepIndex) => {
    setResourceData((prev) => {
      const steps = [...prev.workshop_steps];
      const step = steps[stepIndex];
      const nextOrder = step.step_resources.length + 1;
      step.step_resources.push({
        resource_title: "",
        resource_description: "",
        external_link: "",
        video_duration: "",
        resource_link: "",
        resource_type: "internal",
        order: nextOrder,
        card_image: "",
        resource_file: "",
        is_active: true,
      });
      steps[stepIndex] = step;
      return { ...prev, workshop_steps: steps };
    });
  };

  const handleResourceTypeChange = (sIndex, rIndex, value) => {
    setResourceData((prev) => {
      const updated = [...prev.workshop_steps];
      updated[sIndex].step_resources[rIndex].resource_type = value;

      if (value === "internal") {
        updated[sIndex].step_resources[rIndex].resource_link = "";
      } else if (value === "external") {
        updated[sIndex].step_resources[rIndex].resource_file = null;
      }

      return { ...prev, workshop_steps: updated };
    });
  };

  const handleResourceLinkChange = (sIndex, rIndex, value) => {
    setResourceData((prev) => {
      const updated = [...prev.workshop_steps];
      updated[sIndex].step_resources[rIndex].resource_link = value;
      return { ...prev, workshop_steps: updated };
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e, resourceData);
  };

  const tagOptions = tagList.map((tag) => ({ value: tag.id, label: tag.name }));

  return (
    <Modal isOpen={isOpen} onClose={toggle} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{confirmText + " " + title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxHeight="700px" minH="600px" overflowY="auto">
          <form id="ResourceForm" onSubmit={handleFormSubmit}>
            <FormControl mt={2}>
              <Text>Vault Title</Text>
              <Input
                type="text"
                name="resource_title"
                value={resourceData.resource_title}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Vault Description</Text>
              <Textarea
                name="resource_description"
                value={resourceData.resource_description}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Order</Text>
              <Input
                type="number"
                name="order"
                value={resourceData.order}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Tags</Text>
              <Select
                isMulti
                options={tagOptions}
                value={tagOptions.filter((opt) =>
                  resourceData.tags?.includes(opt.value)
                )}
                onChange={handleMultiSelect}
              />
            </FormControl>

            <FormControl mt={2}>
              <Checkbox
                isChecked={resourceData.is_workshop}
                onChange={(e) =>
                  setResourceData((prev) => ({
                    ...prev,
                    is_workshop: e.target.checked,
                  }))
                }
              >
                Is this a Workshop?
              </Checkbox>
            </FormControl>

            <FormControl mt={2}>
              <Text>Thumbnail</Text>
              <Box
                borderWidth="2px"
                borderStyle="dashed"
                borderColor="gray"
                borderRadius="lg"
                backgroundColor="white"
                width="100%"
                maxW="400px"
                height="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                cursor="pointer"
                _hover={{ borderColor: "blue.300" }}
                position="relative"
                overflow="hidden"
              >
                {preview || resourceData?.card_image ? (
                  <Image
                    src={preview ? preview : resourceData?.card_image}
                    alt="Uploaded preview"
                    maxH="100%"
                    maxW="100%"
                    objectFit="contain"
                    borderRadius="md"
                  />
                ) : (
                  <>
                    <Icon as={FaFileImage} boxSize={10} color="gray.400" />
                    <Text mt={2}>
                      <Text as="span" fontWeight="bold" color="blue.500">
                        Click to upload
                      </Text>{" "}
                      or drag and drop
                    </Text>
                  </>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  opacity="0"
                  position="absolute"
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  name="card_image"
                />
              </Box>
              {preview && (
                <Button
                  leftIcon={<FaTrash />}
                  colorScheme="red"
                  variant="outline"
                  mt={2}
                  onClick={() => removeImage()}
                >
                  Remove File
                </Button>
              )}
            </FormControl>

            <FormControl mt={2}>
              <Text>Video Link</Text>
              <Input
                name="external_link"
                value={resourceData.external_link}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Video Duration</Text>
              <Input
                name="video_duration"
                value={resourceData.video_duration}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Resource Type</Text>
              <Select
                options={[
                  { value: "internal", label: "Internal" },
                  { value: "external", label: "External" },
                ]}
                placeholder="Select type"
                value={[
                  { value: "internal", label: "Internal" },
                  { value: "external", label: "External" },
                ].find((opt) => opt.value === resourceData.resource_type)}
                onChange={(option) =>
                  setResourceData({
                    ...resourceData,
                    resource_type: option.value,
                  })
                }
              />
            </FormControl>

            {resourceData.resource_type === "internal" ? (
              <FormControl mt={2}>
                <Text>Attachment</Text>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv"
                  onChange={(e) => handleFileChange(e)}
                />
                {fileAttached && (
                  <Text mt={1}>
                    {fileAttached}{" "}
                    <Button size="xs" onClick={() => handleRemoveFile()}>
                      Remove
                    </Button>
                  </Text>
                )}
              </FormControl>
            ) : (
              <FormControl mt={2}>
                <Text>Resource Link</Text>
                <Input
                  type="url"
                  name="resource_link"
                  placeholder="Enter external resource link"
                  value={resourceData.resource_link}
                  onChange={(e) =>
                    setResourceData({
                      ...resourceData,
                      resource_link: e.target.value,
                    })
                  }
                />
              </FormControl>
            )}

            {resourceData.is_workshop && (
              <Box mt={5}>
                <Text fontWeight="bold">Workshop Steps</Text>
                {resourceData.workshop_steps
                  .map((step, sIndex) => ({ step, sIndex }))
                  .filter(({ step }) => step.is_active !== false)
                  .map(({ step, sIndex }, visibleIndex) => (
                    <Box key={sIndex} borderWidth={1} p={2} mt={2}>
                      <Flex justify="space-between" align="center">
                        <Text fontWeight={800} fontSize={"25px"}>
                          Step {visibleIndex + 1}
                        </Text>
                        <Button size="xs" onClick={() => removeStep(sIndex)}>
                          Remove Step
                        </Button>
                      </Flex>

                      <Text mt={2}>Step Label</Text>
                      <Input
                        placeholder="Step Label"
                        mt={1}
                        value={step.label}
                        onChange={(e) =>
                          handleStepChange(sIndex, "label", e.target.value)
                        }
                      />

                      <Text mt={2}>Title</Text>
                      <Input
                        placeholder="Step Title"
                        mt={1}
                        value={step.title}
                        onChange={(e) =>
                          handleStepChange(sIndex, "title", e.target.value)
                        }
                      />

                      <Text mt={2}>Description</Text>
                      <Textarea
                        mt={1}
                        value={step.description}
                        onChange={(e) =>
                          handleStepChange(
                            sIndex,
                            "description",
                            e.target.value
                          )
                        }
                      />

                      <Text mt={2}>Order</Text>
                      <Input
                        mt={1}
                        type="number"
                        value={step.order}
                        onChange={(e) =>
                          handleStepChange(
                            sIndex,
                            "order",
                            Number(e.target.value)
                          )
                        }
                      />

                      {step.step_resources
                        .map((res, rIndex) => ({ res, rIndex }))
                        .filter(({ res }) => res.is_active !== false)
                        .map(({ res, rIndex }, resVisibleIndex) => (
                          <Box key={rIndex} borderWidth={1} p={2} mt={2}>
                            <Flex justify="space-between" align="center">
                              <Text fontWeight={800} fontSize={"25px"}>
                                Resource {resVisibleIndex + 1}
                              </Text>
                              <Button
                                size="xs"
                                colorScheme="red"
                                onClick={() =>
                                  removeStepResource(sIndex, rIndex)
                                }
                              >
                                Remove
                              </Button>
                            </Flex>

                            <Text mt={2}>Title</Text>
                            <Input
                              mt={1}
                              placeholder="Title"
                              value={res.resource_title}
                              onChange={(e) =>
                                handleStepResourceChange(
                                  sIndex,
                                  rIndex,
                                  "resource_title",
                                  e.target.value
                                )
                              }
                            />

                            <Text mt={2}>Description</Text>
                            <Textarea
                              mt={1}
                              placeholder="Description"
                              value={res.resource_description}
                              onChange={(e) =>
                                handleStepResourceChange(
                                  sIndex,
                                  rIndex,
                                  "resource_description",
                                  e.target.value
                                )
                              }
                            />

                            <Text mt={2}>Video Link</Text>
                            <Input
                              mt={1}
                              placeholder="Video Link"
                              value={res.external_link}
                              onChange={(e) =>
                                handleStepResourceChange(
                                  sIndex,
                                  rIndex,
                                  "external_link",
                                  e.target.value
                                )
                              }
                            />

                            <Text mt={2}>Video Duration</Text>
                            <Input
                              mt={1}
                              placeholder="Video Duration"
                              value={res.video_duration}
                              onChange={(e) =>
                                handleStepResourceChange(
                                  sIndex,
                                  rIndex,
                                  "video_duration",
                                  e.target.value
                                )
                              }
                            />

                            <Text mt={2}>Order</Text>
                            <Input
                              mt={1}
                              type="number"
                              value={step.order}
                              onChange={(e) =>
                                handleStepResourceChange(
                                  sIndex,
                                  rIndex,
                                  "order",
                                  e.target.value
                                )
                              }
                            />

                            <Text mt={2}>Resource Type</Text>

                            <Select
                              size="sm"
                              name="resource_type"
                              options={[
                                { label: "Internal", value: "internal" },
                                { label: "External", value: "external" },
                              ]}
                              value={
                                res.resource_type === "external"
                                  ? { label: "External", value: "external" }
                                  : { label: "Internal", value: "internal" }
                              }
                              onChange={(option) =>
                                handleResourceTypeChange(
                                  sIndex,
                                  rIndex,
                                  option?.value || "internal"
                                )
                              }
                            />

                            <Text mt={2}>Attachment</Text>
                            {(res.resource_type === "internal" ||
                              res.resource_type === "") && (
                              <>
                                <Input
                                  type="file"
                                  accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv"
                                  mt={2}
                                  onChange={(e) =>
                                    handleFileChange(e, sIndex, rIndex)
                                  }
                                />
                                {res.resource_file && (
                                  <Text mt={1}>
                                    {res.resource_file.name || res.resource_file}{" "}
                                    <Button
                                      size="xs"
                                      onClick={() =>
                                        handleRemoveFile(sIndex, rIndex)
                                      }
                                    >
                                      Remove
                                    </Button>
                                  </Text>
                                )}
                              </>
                            )}

                            {res.resource_type === "external" && (
                              <Input
                                mt={2}
                                name="resource_link"
                                placeholder="Enter resource link"
                                value={res.resource_link || ""}
                                onChange={(e) =>
                                  handleResourceLinkChange(
                                    sIndex,
                                    rIndex,
                                    e.target.value
                                  )
                                }
                              />
                            )}

                            <Text mt={2}>Thumbnail</Text>
                            <Box
                              borderWidth="2px"
                              borderStyle="dashed"
                              borderColor="gray"
                              borderRadius="lg"
                              backgroundColor="white"
                              width="100%"
                              maxW="400px"
                              height="200px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexDirection="column"
                              cursor="pointer"
                              _hover={{ borderColor: "blue.300" }}
                              position="relative"
                              overflow="hidden"
                            >
                              {stepPreviews[`${sIndex}_${rIndex}`] ||
                              res.card_image ? (
                                <Image
                                  src={
                                    stepPreviews[`${sIndex}_${rIndex}`]
                                      ? stepPreviews[`${sIndex}_${rIndex}`]
                                      : res.card_image
                                  }
                                  alt="Uploaded preview"
                                  maxH="100%"
                                  maxW="100%"
                                  objectFit="contain"
                                  borderRadius="md"
                                />
                              ) : (
                                <>
                                  <Icon
                                    as={FaFileImage}
                                    boxSize={10}
                                    color="gray.400"
                                  />
                                  <Text mt={2}>
                                    <Text
                                      as="span"
                                      fontWeight="bold"
                                      color="blue.500"
                                    >
                                      Click to upload
                                    </Text>{" "}
                                    or drag and drop
                                  </Text>
                                </>
                              )}
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleImageChange(e, sIndex, rIndex)
                                }
                                opacity="0"
                                position="absolute"
                                width="100%"
                                height="100%"
                                cursor="pointer"
                              />
                            </Box>
                            {stepPreviews[`${sIndex}_${rIndex}`] && (
                              <Button
                                leftIcon={<FaTrash />}
                                colorScheme="red"
                                variant="outline"
                                mt={2}
                                onClick={() => removeImage(sIndex, rIndex)}
                              >
                                Remove File
                              </Button>
                            )}
                          </Box>
                        ))}
                      <Button
                        mt={2}
                        size="sm"
                        colorScheme="blue"
                        onClick={() => addStepResource(sIndex)}
                      >
                        Add Resource
                      </Button>
                    </Box>
                  ))}
                <Button mt={2} colorScheme="green" onClick={addStep}>
                  Add Step
                </Button>
              </Box>
            )}
          </form>
        </ModalBody>

        <ModalFooter>
          <Button onClick={toggle} isDisabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="ResourceForm" isDisabled={loading} ml={2}>
            {loading ? "Loading..." : confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
